import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { afterPaint } from "@/components/notices/afterPaint";
import "./Dialog.css";

export const DIALOG_EXIT_MS = 280;

export interface DialogProps {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Optional action row pinned under the body. Put form buttons in `children` instead
   *  when the dialog wraps a <form>, so submit still fires. */
  footer?: ReactNode;
  /** Max panel width. */
  width?: string;
  /** Max panel height. */
  maxHeight?: string;
  /** Close when Escape is pressed. Off while a nested dialog owns the key. */
  escapeClose?: boolean;
  /** Optional controls next to the close button (settings gear, etc.). */
  headerActions?: ReactNode;
  /** Slide the footer in when it appears (dirty save bar). */
  slideFooter?: boolean;
  /** Floats beside the panel (section jumpers, etc.). Hidden on narrow viewports. */
  sideRail?: ReactNode;
}

/**
 * Generic modal shell with the same slide-up as NotificationModal.
 * Keep the component mounted and drive `open` so the exit animation can play.
 */
export default function Dialog({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = "26rem",
  maxHeight,
  escapeClose = true,
  headerActions,
  slideFooter = false,
  sideRail,
}: DialogProps) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);
  const leaveTimer = useRef<number | null>(null);
  const hasFooter = footer != null;
  const [footMounted, setFootMounted] = useState(hasFooter);
  const [footVisible, setFootVisible] = useState(hasFooter && !slideFooter);

  const finishClose = (notify: boolean) => {
    setVisible(false);
    if (leaveTimer.current !== null) window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => {
      setMounted(false);
      if (notify) onClose();
    }, DIALOG_EXIT_MS);
  };

  useEffect(() => {
    if (open) {
      setMounted(true);
      setVisible(false);
      return afterPaint(() => setVisible(true));
    }
    if (mounted) finishClose(false);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !escapeClose) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, escapeClose]);

  useEffect(() => {
    if (hasFooter) {
      setFootMounted(true);
      if (!slideFooter) {
        setFootVisible(true);
        return undefined;
      }
      setFootVisible(false);
      return afterPaint(() => setFootVisible(true));
    }
    setFootVisible(false);
    if (!slideFooter) {
      setFootMounted(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setFootMounted(false), DIALOG_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [hasFooter, slideFooter]);

  useEffect(
    () => () => {
      if (leaveTimer.current !== null) window.clearTimeout(leaveTimer.current);
    },
    [],
  );

  if (!mounted) return null;

  return createPortal(
    <div className="al-dialog-root" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        className={`al-dialog-scrim${visible ? " al-dialog-scrim--visible" : ""}`}
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        className={`al-dialog-cluster${visible ? " al-dialog-cluster--visible" : ""}`}
        style={{ width: `min(${width}, 100%)` }}
      >
        <div
          className="al-dialog-panel"
          style={maxHeight ? { maxHeight } : undefined}
        >
          <div className="al-dialog-head">
            <div>
              <h2 className="al-dialog-title">{title}</h2>
              {subtitle ? <div className="al-dialog-sub">{subtitle}</div> : null}
            </div>
            <div className="al-dialog-head-actions">
              {headerActions}
              {headerActions ? <span className="al-dialog-head-rule" aria-hidden="true" /> : null}
              <button type="button" className="al-dialog-close" aria-label="Close" onClick={onClose}>
                ×
              </button>
            </div>
          </div>
          <div className="al-dialog-body">{children}</div>
          {footMounted ? (
            <div className={`al-dialog-foot-slot${footVisible ? " al-dialog-foot-slot--open" : ""}${slideFooter ? " al-dialog-foot-slot--slide" : ""}`}>
              <div className="al-dialog-foot-clip">
                <div className={`al-dialog-foot${slideFooter ? " al-dialog-foot--center" : ""}`}>{footer}</div>
              </div>
            </div>
          ) : null}
        </div>
        {sideRail ? <aside className="al-dialog-rail">{sideRail}</aside> : null}
      </div>
    </div>,
    document.body,
  );
}
